import { ORPCError } from "@orpc/client";
import {
  parseVocabularyPaste,
  VOCABULARY_MAX_TERMS,
  type VocabularyTerm,
  type VocabularyUpdate,
  vocabularyKey,
  vocabularySoundsLikeSchema,
  vocabularyTermTextSchema,
} from "@rosc/contracts";
import {
  BookOpenText,
  Check,
  ClipboardList,
  Info,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { type FormEvent, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  useVocabulary,
  type VocabularyController,
} from "@/lib/vocabulary-queries";

// Same text size as ROSC's inputs; 44px tall on phones for touch.
const FIELD =
  "h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base text-foreground shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:h-10";
const ICON_BUTTON =
  "size-11 shrink-0 text-muted-foreground hover:text-foreground sm:size-9";

type Problem =
  | "duplicate"
  | "full"
  | "invalid"
  | "unavailable"
  | "failed"
  | "tooLong";

const problemOf = (error: unknown): Problem => {
  if (error instanceof ORPCError) {
    if (error.code === "CONFLICT")
      return /limited/i.test(error.message) ? "full" : "duplicate";
    if (error.code === "BAD_REQUEST") return "invalid";
    if (error.code === "SERVICE_UNAVAILABLE") return "unavailable";
  }
  return "failed";
};
const checkTerm = (value: string): Problem | null => {
  const parsed = vocabularyTermTextSchema.safeParse(value);
  if (parsed.success) return null;
  return Array.from(value.trim()).length > 60 ? "tooLong" : "invalid";
};
const checkSoundsLike = (value: string): Problem | null => {
  if (!value.trim()) return null;
  const parsed = vocabularySoundsLikeSchema.safeParse(value);
  if (parsed.success) return null;
  return Array.from(value.trim()).length > 60 ? "tooLong" : "invalid";
};

export const VocabularySettings = (): JSX.Element => (
  <VocabularyCard controller={useVocabulary()} />
);

/** "Vocabulary": the clinician's frequently used terms. */
export const VocabularyCard = ({
  controller,
}: {
  controller: VocabularyController;
}): JSX.Element => {
  const { t } = useTranslation();
  const { terms, loading, failed, retry } = controller;
  const ids = {
    term: useId(),
    soundsLike: useId(),
    search: useId(),
    hint: useId(),
  };
  const [term, setTerm] = useState("");
  const [soundsLike, setSoundsLike] = useState("");
  const [showSoundsLike, setShowSoundsLike] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [pasteOpen, setPasteOpen] = useState(false);
  const termRef = useRef<HTMLInputElement>(null);

  const full = terms.length >= VOCABULARY_MAX_TERMS;
  // Newest first: the terms just added stay in view.
  const ordered = useMemo(() => [...terms].reverse(), [terms]);
  const visible = useMemo(() => {
    const needle = vocabularyKey(query);
    if (!needle) return ordered;
    return ordered.filter(
      (item) =>
        vocabularyKey(item.term).includes(needle) ||
        vocabularyKey(item.soundsLike ?? "").includes(needle),
    );
  }, [ordered, query]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const invalid = checkTerm(term) ?? checkSoundsLike(soundsLike);
    if (invalid) {
      setProblem(invalid);
      termRef.current?.focus();
      return;
    }
    if (
      terms.some((item) => vocabularyKey(item.term) === vocabularyKey(term))
    ) {
      setProblem("duplicate");
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await controller.add([
        {
          term: vocabularyTermTextSchema.parse(term),
          soundsLike: soundsLike.trim()
            ? vocabularySoundsLikeSchema.parse(soundsLike)
            : null,
        },
      ]);
      setTerm("");
      setSoundsLike("");
      setShowSoundsLike(false);
      termRef.current?.focus();
    } catch (error) {
      setProblem(problemOf(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: VocabularyTerm): Promise<void> => {
    try {
      await controller.remove(item.id);
      toast({
        title: t("settings.vocabulary.removed", { term: item.term }),
        action: (
          <ToastAction
            altText={t("settings.vocabulary.undo")}
            onClick={() =>
              void controller
                .add([{ term: item.term, soundsLike: item.soundsLike }])
                .catch(() => undefined)
            }
          >
            {t("settings.vocabulary.undo")}
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t(`settings.vocabulary.problems.${problemOf(error)}`, {
          max: VOCABULARY_MAX_TERMS,
        }),
      });
    }
  };

  return (
    <section
      aria-labelledby="vocabulary-title"
      data-testid="vocabulary-settings"
      className="bg-background sm:rounded-[15px] sm:border sm:border-border sm:p-6"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="hidden size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 sm:flex">
          <BookOpenText aria-hidden="true" className="size-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3
              id="vocabulary-title"
              className="text-lg font-bold leading-snug"
            >
              {t("settings.vocabulary.title")}
            </h3>
            {!loading && !failed && (
              <span
                data-testid="vocabulary-count"
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums",
                  full
                    ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {t("settings.vocabulary.count", {
                  count: terms.length,
                  max: VOCABULARY_MAX_TERMS,
                })}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground sm:text-base">
            {t("settings.vocabulary.subtitle")}
          </p>
        </div>
      </div>

      <form
        onSubmit={(event) => void submit(event)}
        className="mt-5 space-y-3"
        noValidate
      >
        <div
          className={cn(
            "grid gap-3",
            showSoundsLike
              ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
              : "sm:grid-cols-[minmax(0,1fr)_auto]",
          )}
        >
          <div className="min-w-0 space-y-1.5">
            <label htmlFor={ids.term} className="text-sm font-semibold">
              {t("settings.vocabulary.term.label")}
            </label>
            <input
              id={ids.term}
              ref={termRef}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setProblem(null);
              }}
              maxLength={80}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t("settings.vocabulary.term.placeholder")}
              aria-invalid={problem ? true : undefined}
              aria-describedby={ids.hint}
              disabled={full || saving}
              className={FIELD}
            />
          </div>
          {showSoundsLike && (
            <div className="min-w-0 space-y-1.5">
              <label htmlFor={ids.soundsLike} className="text-sm font-semibold">
                {t("settings.vocabulary.soundsLike.label")}
              </label>
              <input
                id={ids.soundsLike}
                value={soundsLike}
                onChange={(event) => {
                  setSoundsLike(event.target.value);
                  setProblem(null);
                }}
                maxLength={80}
                autoComplete="off"
                spellCheck={false}
                placeholder={t("settings.vocabulary.soundsLike.placeholder")}
                disabled={full || saving}
                className={FIELD}
              />
            </div>
          )}
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={full || saving || !term.trim()}
              className="h-11 w-full px-5 sm:h-10 sm:w-auto"
            >
              <Plus aria-hidden="true" />
              {t("settings.vocabulary.add")}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {!showSoundsLike && (
            <button
              type="button"
              onClick={() => setShowSoundsLike(true)}
              disabled={full}
              className="min-h-11 text-sm font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50 sm:min-h-0"
            >
              {t("settings.vocabulary.soundsLike.show")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPasteOpen(true)}
            disabled={full}
            className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50 sm:min-h-0"
          >
            <ClipboardList aria-hidden="true" className="size-4" />
            {t("settings.vocabulary.paste.open")}
          </button>
        </div>
        <p
          id={ids.hint}
          role={problem ? "alert" : undefined}
          className={cn(
            "text-xs",
            problem ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {problem
            ? t(`settings.vocabulary.problems.${problem}`, {
                max: VOCABULARY_MAX_TERMS,
              })
            : full
              ? t("settings.vocabulary.problems.full", {
                  max: VOCABULARY_MAX_TERMS,
                })
              : t("settings.vocabulary.term.hint")}
        </p>
      </form>

      <div className="mt-5">
        {loading ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : failed ? (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-lg border border-border p-4 text-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <span>{t("settings.vocabulary.loadFailed")}</span>
            <Button
              type="button"
              variant="outline"
              onClick={retry}
              className="h-11 sm:h-9"
            >
              {t("settings.vocabulary.retry")}
            </Button>
          </div>
        ) : terms.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm font-semibold">
              {t("settings.vocabulary.empty.title")}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              {t("settings.vocabulary.empty.body")}
            </p>
          </div>
        ) : (
          <>
            {terms.length > 8 && (
              <div className="relative mb-3">
                <label htmlFor={ids.search} className="sr-only">
                  {t("settings.vocabulary.search")}
                </label>
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  id={ids.search}
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("settings.vocabulary.search")}
                  className={cn(FIELD, "pl-9")}
                />
              </div>
            )}
            <ul
              aria-label={t("settings.vocabulary.listLabel")}
              className="divide-y divide-border overflow-hidden rounded-lg border border-border"
            >
              {visible.map((item) => (
                <TermRow
                  key={item.id}
                  item={item}
                  terms={terms}
                  onSave={(body) => controller.update(item.id, body)}
                  onRemove={() => void remove(item)}
                />
              ))}
              {visible.length === 0 && (
                <li className="px-4 py-3 text-sm text-muted-foreground">
                  {t("settings.vocabulary.noMatch")}
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      <div className="mt-5 flex gap-3 rounded-lg bg-muted/60 p-3 text-sm text-foreground/90">
        <Info
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        />
        <div className="space-y-1">
          <p>{t("settings.vocabulary.how.notes")}</p>
          <p className="text-muted-foreground">
            {t("settings.vocabulary.how.transcription")}
          </p>
        </div>
      </div>

      <PasteDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        terms={terms}
        onAdd={controller.add}
      />
    </section>
  );
};

const TermRow = ({
  item,
  terms,
  onSave,
  onRemove,
}: {
  item: VocabularyTerm;
  terms: VocabularyTerm[];
  onSave: (body: VocabularyUpdate) => Promise<VocabularyTerm>;
  onRemove: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [term, setTerm] = useState(item.term);
  const [soundsLike, setSoundsLike] = useState(item.soundsLike ?? "");
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);
  const ids = { term: useId(), soundsLike: useId(), error: useId() };

  const start = (): void => {
    setTerm(item.term);
    setSoundsLike(item.soundsLike ?? "");
    setProblem(null);
    setEditing(true);
  };
  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const invalid = checkTerm(term) ?? checkSoundsLike(soundsLike);
    if (invalid) return setProblem(invalid);
    if (
      terms.some(
        (other) =>
          other.id !== item.id &&
          vocabularyKey(other.term) === vocabularyKey(term),
      )
    )
      return setProblem("duplicate");
    setSaving(true);
    try {
      await onSave({
        term: vocabularyTermTextSchema.parse(term),
        soundsLike: soundsLike.trim()
          ? vocabularySoundsLikeSchema.parse(soundsLike)
          : null,
      });
      setEditing(false);
    } catch (error) {
      setProblem(problemOf(error));
    } finally {
      setSaving(false);
    }
  };

  if (editing)
    return (
      <li className="bg-muted/40 px-3 py-3 sm:px-4">
        <form
          onSubmit={(event) => void save(event)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setEditing(false);
          }}
          className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
          noValidate
        >
          <div className="min-w-0 space-y-1">
            <label htmlFor={ids.term} className="text-xs font-semibold">
              {t("settings.vocabulary.term.label")}
            </label>
            <input
              id={ids.term}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setProblem(null);
              }}
              // biome-ignore lint/a11y/noAutofocus: the row just switched to editing
              autoFocus
              maxLength={80}
              spellCheck={false}
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? ids.error : undefined}
              className={FIELD}
            />
          </div>
          <div className="min-w-0 space-y-1">
            <label htmlFor={ids.soundsLike} className="text-xs font-semibold">
              {t("settings.vocabulary.soundsLike.label")}
            </label>
            <input
              id={ids.soundsLike}
              value={soundsLike}
              onChange={(event) => {
                setSoundsLike(event.target.value);
                setProblem(null);
              }}
              maxLength={80}
              spellCheck={false}
              placeholder={t("settings.vocabulary.soundsLike.placeholder")}
              className={FIELD}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={saving}
              className="h-11 flex-1 sm:h-10 sm:flex-none"
            >
              <Check aria-hidden="true" />
              {t("settings.vocabulary.save")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(false)}
              className="h-11 flex-1 sm:h-10 sm:flex-none"
            >
              <X aria-hidden="true" />
              {t("settings.vocabulary.cancel")}
            </Button>
          </div>
          {problem && (
            <p
              id={ids.error}
              role="alert"
              className="text-xs text-destructive sm:col-span-3"
            >
              {t(`settings.vocabulary.problems.${problem}`, {
                max: VOCABULARY_MAX_TERMS,
              })}
            </p>
          )}
        </form>
      </li>
    );

  return (
    <li className="flex items-center gap-2 py-1 pl-4 pr-1 sm:pr-2">
      <div className="min-w-0 flex-1 py-1.5">
        <p className="wrap-break-word font-medium leading-snug" lang="und">
          {item.term}
        </p>
        {item.soundsLike && (
          <p className="wrap-break-word text-xs text-muted-foreground">
            {t("settings.vocabulary.soundsLike.shown", {
              value: item.soundsLike,
            })}
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={start}
        aria-label={t("settings.vocabulary.edit", { term: item.term })}
        className={ICON_BUTTON}
      >
        <Pencil aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label={t("settings.vocabulary.delete", { term: item.term })}
        className={cn(ICON_BUTTON, "hover:text-destructive")}
      >
        <Trash2 aria-hidden="true" />
      </Button>
    </li>
  );
};

const PasteDialog = ({
  open,
  onOpenChange,
  terms,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  terms: VocabularyTerm[];
  onAdd: VocabularyController["add"];
}): JSX.Element => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();
  const parsed = useMemo(() => parseVocabularyPaste(text), [text]);
  const saved = useMemo(
    () => new Set(terms.map((item) => vocabularyKey(item.term))),
    [terms],
  );
  const fresh = parsed.terms.filter((item) => !saved.has(vocabularyKey(item)));
  const already = parsed.terms.length - fresh.length;
  const room = VOCABULARY_MAX_TERMS - terms.length;
  const tooMany = fresh.length > room;

  const close = (next: boolean): void => {
    onOpenChange(next);
    if (!next) {
      setText("");
      setProblem(null);
    }
  };
  const add = async (): Promise<void> => {
    if (!fresh.length || tooMany) return;
    setSaving(true);
    setProblem(null);
    try {
      const { added } = await onAdd(fresh.map((term) => ({ term })));
      toast({
        variant: "success",
        title: t("settings.vocabulary.paste.added", { count: added }),
      });
      close(false);
    } catch (error) {
      setProblem(problemOf(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="vocabulary-paste"
        className="max-h-[90dvh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto rounded-xl"
      >
        <DialogHeader className="text-left">
          <DialogTitle>{t("settings.vocabulary.paste.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.vocabulary.paste.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor={id} className="sr-only">
            {t("settings.vocabulary.paste.title")}
          </label>
          <Textarea
            id={id}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setProblem(null);
            }}
            rows={8}
            spellCheck={false}
            placeholder={t("settings.vocabulary.paste.placeholder")}
            className="min-h-44 resize-y bg-background text-base"
          />
          <ul className="space-y-1 text-sm" aria-live="polite">
            <li className="font-medium">
              {t("settings.vocabulary.paste.new", { count: fresh.length })}
            </li>
            {already > 0 && (
              <li className="text-muted-foreground">
                {t("settings.vocabulary.paste.already", { count: already })}
              </li>
            )}
            {parsed.invalid.length > 0 && (
              <li className="wrap-break-word text-destructive">
                {t("settings.vocabulary.paste.invalid", {
                  count: parsed.invalid.length,
                  terms: parsed.invalid.slice(0, 3).join(", "),
                })}
              </li>
            )}
            {tooMany && (
              <li className="text-destructive">
                {t("settings.vocabulary.paste.tooMany", {
                  count: room,
                  max: VOCABULARY_MAX_TERMS,
                })}
              </li>
            )}
            {problem && (
              <li role="alert" className="text-destructive">
                {t(`settings.vocabulary.problems.${problem}`, {
                  max: VOCABULARY_MAX_TERMS,
                })}
              </li>
            )}
          </ul>
        </div>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => close(false)}
            className="h-11 sm:h-9"
          >
            {t("settings.vocabulary.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void add()}
            disabled={saving || !fresh.length || tooMany}
            className="h-11 sm:h-9"
          >
            {t("settings.vocabulary.paste.submit", { count: fresh.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
